import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Download, PenLine, ScrollText, Sparkles } from 'lucide-react';
import { Section, PartiesSection, PriceSection, DeliverySection } from '@/components/quote/QuoteDocument';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuoteSignPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signerTitle, setSignerTitle] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [authority, setAuthority] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [terms, setTerms] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/sales/quotes/public/${token}`).then(r => { if (!r.ok) throw new Error('Offerten hittades inte'); return r.json(); }),
      fetch(`${API}/api/sales/terms`).then(r => r.ok ? r.json() : { terms_summary: '' }),
    ])
      .then(([q, t]) => { setQuote(q); setTerms(t.terms_summary || ''); if (q.status === 'signed') setSigned(true); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas signature drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    canvas.width = rect.width * scale;
    canvas.height = rect.height * scale;
    ctx.scale(scale, scale);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: clientX - r.left, y: clientY - r.top };
    };

    const start = (e) => {
      e.preventDefault();
      drawingRef.current = true;
      setHasDrawn(true);
      lastPosRef.current = getPos(e);
    };
    const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPosRef.current = pos;
    };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [quote, signed]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(signerEmail.trim());
  const canSign = signerName.trim() && signerTitle.trim() && emailOk && authority && termsAccepted && hasDrawn;

  const submitSignature = async () => {
    if (!canSign) { toast.error('Fyll i alla uppgifter och signera'); return; }
    const sigData = canvasRef.current.toDataURL('image/png');
    setSigning(true);
    try {
      const r = await fetch(`${API}/api/sales/quotes/public/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signed_by: signerName.trim(), signer_title: signerTitle.trim(), signer_email: signerEmail.trim(), signature_data: sigData,
          authority_confirmed: authority, terms_accepted: termsAccepted,
        }),
      });
      if (r.ok) {
        setSigned(true);
        setQuote(q => ({ ...q, signed_by: signerName.trim(), signer_title: signerTitle.trim(), signer_email: signerEmail.trim(), signed_at: new Date().toISOString(), signed_version: q.version }));
        toast.success('Tack! Offerten är godkänd och beställningen lagd.');
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Kunde inte signera');
      }
    } catch {
      toast.error('Nätverksfel');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-slate-500">Laddar offert...</div></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen"><div className="text-red-500">{error}</div></div>;
  if (!quote) return null;

  const Check2 = ({ checked, onChange, children, testId }) => (
    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 p-3 hover:bg-slate-50" data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0f172a]" />
      <span className="text-sm text-slate-700">{children}</span>
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="quote-sign-page">
      <header className="bg-[#0f172a] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-2xl sm:text-3xl font-black tracking-tight">VAPELARM</p>
            <p className="text-sm text-slate-300">Intelligent vape-detektion för skolor</p>
          </div>
          <div className="sm:text-right">
            <p className="text-lg font-bold" data-testid="quote-number">Offert {quote.quote_number}</p>
            <p className="text-xs text-slate-300">{quote.school_name}{quote.version ? ` · version ${quote.version}` : ''}</p>
            {signed && <span className="inline-block mt-1 rounded-full bg-emerald-500 px-3 py-0.5 text-xs font-semibold">Signerad</span>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-5 sm:space-y-6">
        <PartiesSection q={quote} />
        {quote.solution_text && (
          <Section title="Om lösningen" icon={Sparkles} testId="quote-solution">
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-line">{quote.solution_text}</p>
          </Section>
        )}
        <PriceSection q={quote} />
        <DeliverySection q={quote} />

        <Section title="Avtalsvillkor" icon={ScrollText} testId="quote-terms">
          {quote.notes && (
            <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-500 mb-1">Kundspecifika noteringar</p>
              <p className="text-sm whitespace-pre-line text-slate-700">{quote.notes}</p>
            </div>
          )}
          {quote.standard_terms && <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{quote.standard_terms}</p>}
          {terms && (
            <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
              <button className="w-full flex items-center justify-between p-3 text-left text-sm font-medium bg-slate-50 hover:bg-slate-100 transition-colors" onClick={() => setShowTerms(!showTerms)} data-testid="toggle-terms-btn">
                <span>Vapelarm AB:s Allmänna villkor</span>
                <span className="text-xs text-slate-500">{showTerms ? 'Dölj' : 'Visa'}</span>
              </button>
              {showTerms && <div className="p-3 text-xs text-slate-600 whitespace-pre-line border-t max-h-72 overflow-y-auto">{terms}</div>}
            </div>
          )}
        </Section>

        {signed ? (
          <section className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6 text-center" data-testid="quote-signed-box">
            <Check className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
            <h2 className="text-lg font-bold text-emerald-900">Offerten är godkänd och beställningen är lagd</h2>
            <p className="text-sm text-emerald-800 mt-1">
              Signerad av {quote.signed_by}{quote.signer_title ? `, ${quote.signer_title}` : ''}
              {quote.signed_at ? ` · ${String(quote.signed_at).slice(0, 16).replace('T', ' ')}` : ''}
              {quote.signed_version ? ` · version ${quote.signed_version}` : ''}
            </p>
            {quote.signer_email && (
              <p className="text-sm text-emerald-800 mt-2" data-testid="order-confirmation-info">
                En orderbekräftelse med den signerade offerten skickas till {[...new Set([quote.contact_email, quote.signer_email].filter(Boolean))].join(' och ')}.
              </p>
            )}
            <Button variant="outline" className="mt-4" onClick={() => window.open(`${API}/api/sales/quotes/public/${token}/pdf`, '_blank')} data-testid="download-signed-pdf">
              <Download className="h-4 w-4 mr-1" /> Ladda ner PDF
            </Button>
          </section>
        ) : (
          <section className="bg-white rounded-2xl border-2 border-[#0f172a] p-5 sm:p-7 space-y-4" data-testid="quote-sign-section">
            <div>
              <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-900"><PenLine className="h-5 w-5" /> Godkänn offert och lägg beställning</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{quote.sign_declaration}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-500">Fullständigt namn *</label><Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="För- och efternamn" data-testid="signer-name-input" /></div>
              <div><label className="text-xs font-medium text-slate-500">Befattning *</label><Input value={signerTitle} onChange={e => setSignerTitle(e.target.value)} placeholder="T.ex. Rektor" data-testid="signer-title-input" /></div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-500">E-post * – orderbekräftelsen skickas hit</label><Input type="email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)} placeholder="namn@kommun.se" data-testid="signer-email-input" /></div>
            </div>
            <Check2 checked={authority} onChange={setAuthority} testId="authority-checkbox">
              Jag bekräftar att jag har rätt att beställa för <strong>{quote.school_name}</strong>.
            </Check2>
            <Check2 checked={termsAccepted} onChange={setTermsAccepted} testId="terms-checkbox">
              Jag har läst och godkänner offertens avtalsvillkor och <button className="text-blue-700 underline" onClick={e => { e.preventDefault(); setShowTerms(true); }}>Vapelarm AB:s Allmänna villkor</button>.
            </Check2>
            <div>
              <div className="text-xs font-medium text-slate-500 mb-1">Signatur * – rita med finger eller mus</div>
              <canvas ref={canvasRef} className="w-full h-36 border-2 border-dashed border-slate-300 rounded-lg bg-white cursor-crosshair touch-none" data-testid="signature-canvas" />
              <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={clearSignature} data-testid="clear-signature-btn">Rensa signatur</Button>
            </div>
            <Button className="w-full bg-[#0f172a] hover:bg-[#1e293b] h-12 text-base" onClick={submitSignature} disabled={signing || !canSign} data-testid="sign-quote-btn">
              {signing ? 'Signerar...' : 'Godkänn offert och lägg beställning'}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">Signeringen registreras med tidpunkt, offertversion och godkända villkor.</p>
          </section>
        )}

        <footer className="text-center text-[11px] text-slate-500 pb-6">
          {quote.company && `${quote.company.name} · Org.nr ${quote.company.org_number} · ${quote.company.address} · ${quote.company.email} · ${quote.company.phone}`}
        </footer>
      </main>
    </div>
  );
}
